<script lang="ts" setup>
import { SplitPanel } from '@directus/vue-split-panel';
import { computed, useTemplateRef } from 'vue';
import { PANEL_DEFAULT_SIZE, PANEL_MAX_SIZE, PANEL_MIN_SIZE, SplitPanelPane } from '#layers/ui/public';
import WorkspaceChromeDivider from './WorkspaceChromeDivider.vue';

// Multiple root nodes: attrs go to the split panel
defineOptions({ inheritAttrs: false });

/** End pane beside the content, or a drawer when it doesn't fit. Drive its models with `useSplitPanelDrawer`. */
const { split, maxSize = PANEL_MAX_SIZE } = defineProps<{
	split: boolean;
	maxSize?: number;
}>();

const size = defineModel<number>('size', { required: true });
const collapsed = defineModel<boolean>('collapsed', { required: true });
const drawerOpen = defineModel<boolean>('drawerOpen', { required: true });

const splitContainer = useTemplateRef<{ $el: HTMLElement }>('splitContainer');
const drawerContainer = useTemplateRef<HTMLElement>('drawerContainer');

const teleportTarget = computed(() => (split ? splitContainer.value?.$el : drawerContainer.value));
</script>

<template>
	<SplitPanel
		v-model:size="size"
		v-model:collapsed="collapsed"
		v-bind="$attrs"
		size-unit="px"
		collapsible
		:collapse-threshold="70"
		:min-size="PANEL_MIN_SIZE"
		:max-size
		:snap-points="[PANEL_DEFAULT_SIZE]"
		:snap-threshold="6"
		:ui="{
			start: 'flex flex-col',
			divider: 'focus-visible:ring-1',
		}"
		divider-hit-area="24px"
		:transition-duration="150"
		primary="end"
		:disabled="!split"
	>
		<template #start>
			<slot />
		</template>

		<template #divider>
			<WorkspaceChromeDivider v-show="split" :collapsed placement="end" />
		</template>

		<template v-if="split" #end>
			<SplitPanelPane ref="splitContainer" :size side="end" />
		</template>
	</SplitPanel>

	<DDrawer
		v-model:open="drawerOpen"
		direction="right"
		:handle="false"
		:ui="{ content: `w-[calc(100vw-var(--spacebar-width))] sm:w-90` }"
	>
		<template #content>
			<div class="flex size-full flex-col overflow-hidden rounded-lg bg-default ring ring-default">
				<div ref="drawerContainer" class="flex h-full flex-col" />
			</div>
		</template>
	</DDrawer>

	<div v-if="$slots.pane" class="hidden">
		<Teleport :to="teleportTarget" :disabled="!teleportTarget" defer>
			<slot name="pane" />
		</Teleport>
	</div>
</template>
